import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader } from "@mantine/core";
import { IconFilter } from "@tabler/icons-react";
import { fetchBrowseDirectory } from "../../../actions/documentActions";
import { iconMap } from "../../../utilities/utilities";
import { contextItemIcon } from "../contextItemIcon";
import { contextItemKey, filterMatches } from "../contextItemUtils";
import ClearableTextInput from "../ClearableTextInput";
import pillButtonStyle from "../pillButtonStyle";
import RowSubtitle from "../RowSubtitle";
import BrowseRow from "./BrowseRow";
import BrowseBreadcrumbs from "./BrowseBreadcrumbs";
import BrowseSortMenu from "./BrowseSortMenu";

const CONTENT_ROOTS = [
	{ key: "docs", label: "Documents" },
	{ key: "email", label: "Email" },
	{ key: "notes", label: "Notes" },
];

// Same two sort fields (name-ish, date-ish) for every content root, but
// labeled with each root's actual field name — emails sort by subject line
// and email date, not a generic "name"/"modified date".
const sortOptionsFor = (contentRoot) => {
	const nameLabel = contentRoot === "email" ? "Subject" : "Name";
	const dateLabel = contentRoot === "email" ? "Email Date" : "Modified Date";
	return [
		{ key: "nameAsc", label: `${nameLabel} (A-Z)` },
		{ key: "nameDesc", label: `${nameLabel} (Z-A)` },
		{ key: "modifiedDesc", label: `${dateLabel} (Newest)` },
		{ key: "modifiedAsc", label: `${dateLabel} (Oldest)` },
	];
};

// listing item `type` ("File"/"Email"/"Note") -> add-to-context `itemType`
const ITEM_TYPE_MAP = { File: "Document", Email: "Email", Note: "Matter Note" };

const getTitle = (item) => {
	switch (item.type) {
		case "Folder":
		case "File":
			return item.filename;
		case "Email":
			return item.subjectline;
		case "Note":
			return item.name;
		default:
			return "";
	}
};

const getSubtitle = (item) => {
	if (item.type === "File") {
		return (
			[item.documentType, item.documentStatus]
				.filter(Boolean)
				.join(" · ") || null
		);
	}
	if (item.type === "Email") {
		const parts = [];
		if (item.emailFrom) parts.push(`From: ${item.emailFrom}`);
		if (item.emailTo) parts.push(`To: ${item.emailTo}`);
		return parts.length ? parts.join(" · ") : null;
	}
	return null;
};

const getModifiedValue = (item) =>
	item.lastModified ||
	item.date ||
	item.modifiedOn ||
	item.createdAt ||
	item.createdOn ||
	null;

const isTrashed = (item) => {
	switch (item.type) {
		case "Folder":
			return item.folderLifecycleStatus === "trash";
		case "File":
			return item.documentLifecycleStatus === "trash";
		case "Email":
			return item.emailLifecycleStatus === "trash";
		case "Note":
			return item.matterNoteLifecycleStatus === "trash";
		default:
			return false;
	}
};

const sortItems = (items, sort) => {
	const folders = items.filter((i) => i.type === "Folder");
	const rest = items.filter((i) => i.type !== "Folder");
	folders.sort((a, b) => getTitle(a).localeCompare(getTitle(b)));
	rest.sort((a, b) => {
		switch (sort) {
			case "nameDesc":
				return getTitle(b).localeCompare(getTitle(a));
			case "modifiedDesc":
				return (
					new Date(getModifiedValue(b) || 0) -
					new Date(getModifiedValue(a) || 0)
				);
			case "modifiedAsc":
				return (
					new Date(getModifiedValue(a) || 0) -
					new Date(getModifiedValue(b) || 0)
				);
			case "nameAsc":
			default:
				return getTitle(a).localeCompare(getTitle(b));
		}
	});
	return [...folders, ...rest];
};

// Folder/document/email/note navigator for one matter (or the Public/Private
// Firm Docs pseudo-matter). Documents/Email/Notes are three separate folder
// trees server-side (see fetchBrowseDirectory), so — mirroring the real
// Matter page's own tabs — this renders them as sub-tabs rather than one
// merged tree. Firm Docs is documents-only, so it skips the sub-tabs.
const MatterFolderBrowser = ({
	matter,
	onBack,
	existingItems,
	selected,
	onToggle,
	onPreview,
	browseState,
	onUpdateBrowseState,
	pushBackStep,
}) => {
	// contentRoot/pathSegments/folderSort are lifted to AiAddContextModal (as
	// browseState) so this position survives the modal closing/reopening —
	// BrowseTab initializes contentRoot/pathSegments whenever a new matter is
	// selected, so this component can just read/write them as-is
	const { contentRoot, pathSegments, folderSort: sort } = browseState;
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(true);
	// bumped by the refresh button to force the effect below to refetch the
	// current folder without otherwise changing where we're looking
	const [refreshNonce, setRefreshNonce] = useState(0);
	// filters the current folder's contents by name/ID — local (not lifted
	// to browseState) since it's reset below every time the folder/tab
	// actually changes, unlike the rest of the browse position
	const [filterText, setFilterText] = useState("");
	const listRef = useRef(null);

	const apiMatterID = matter.isFirmDocs ? 0 : matter.id;
	const basepath = `${contentRoot}/${
		pathSegments.length ? pathSegments.join("/") + "/" : ""
	}`;
	const handleRefresh = () => setRefreshNonce((n) => n + 1);

	useEffect(() => {
		let stale = false;
		setLoading(true);
		// the list container's scroll position otherwise survives navigating
		// into/out of a folder (same DOM node, just new contents), which can
		// leave the new folder scrolled out of view and look like the click
		// did nothing — reset it every time the folder actually changes
		if (listRef.current) listRef.current.scrollTop = 0;
		setFilterText("");
		fetchBrowseDirectory(apiMatterID, basepath)
			.then((res) => {
				if (stale) return;
				setItems(res.items || []);
				setLoading(false);
			})
			.catch(() => {
				if (!stale) {
					setItems([]);
					setLoading(false);
				}
			});
		return () => {
			stale = true;
		};
	}, [apiMatterID, basepath, refreshNonce]);

	const activeItems = useMemo(
		() =>
			sortItems(
				items.filter((i) => !isTrashed(i)),
				sort
			),
		[items, sort]
	);

	const visibleItems = useMemo(() => {
		const text = filterText.trim().toLowerCase();
		if (!text) return activeItems;
		return activeItems.filter((rawItem) =>
			filterMatches(text, getTitle(rawItem), rawItem.id)
		);
	}, [activeItems, filterText]);

	const existingKeys = new Set(
		(existingItems || []).map((item) => contextItemKey(item))
	);

	// Every navigation step here records how to undo itself onto the shared
	// Back stack (see AiAddContextModal) before applying the change, so the
	// browser/mouse Back button — or the breadcrumb's own back-chevron,
	// which just calls history.back() — steps back through these one at a
	// time. Never touches the URL.
	const navigate = (patch) => {
		pushBackStep(() => onUpdateBrowseState({ contentRoot, pathSegments }));
		onUpdateBrowseState(patch);
	};

	const switchContentRoot = (key) => {
		navigate({ contentRoot: key, pathSegments: [] });
	};

	const crumbs = [];
	if (onBack) crumbs.push({ label: "All Matters", onClick: onBack });
	crumbs.push({
		label: matter.name,
		onClick: () =>
			navigate({
				pathSegments: [],
				...(matter.isFirmDocs ? {} : { contentRoot: "docs" }),
			}),
	});
	// no separate Documents/Email/Notes crumb — the tab bar below already
	// shows which one is active, and clicking the matter name above already
	// goes there
	pathSegments.forEach((seg, i) => {
		crumbs.push({
			label: seg,
			onClick: () =>
				navigate({ pathSegments: pathSegments.slice(0, i + 1) }),
		});
	});

	// Shown/enabled whenever there's somewhere to go back to; the click
	// itself just triggers a real Back (see AiAddContextModal's popstate
	// listener, which is what actually runs the matching undo).
	const canGoBack = pathSegments.length > 0 || !!onBack;

	return (
		<div
			style={{ display: "flex", flexDirection: "column", height: "100%" }}
		>
			<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
				<ClearableTextInput
					style={{ flex: 1 }}
					placeholder="Filter by name or ID..."
					leftSection={<IconFilter size={16} />}
					value={filterText}
					onChange={(e) => setFilterText(e.currentTarget.value)}
					onClear={() => setFilterText("")}
				/>
				<BrowseSortMenu
					sortOptions={sortOptionsFor(contentRoot)}
					activeSort={sort}
					onChange={(key) => onUpdateBrowseState({ folderSort: key })}
					onRefresh={handleRefresh}
				/>
			</div>
			<div style={{ marginTop: 12 }}>
				<BrowseBreadcrumbs
					segments={crumbs}
					onBack={canGoBack ? () => window.history.back() : undefined}
				/>
			</div>
			{!matter.isFirmDocs ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
					{CONTENT_ROOTS.map((root) => (
						<button
							key={root.key}
							type="button"
							onClick={() => switchContentRoot(root.key)}
							style={pillButtonStyle(contentRoot === root.key)}
						>
							{root.label}
						</button>
					))}
				</div>
			) : null}
			<div
				ref={listRef}
				style={{
					flex: 1,
					minHeight: 0,
					overflowY: "auto",
					marginTop: 8,
				}}
			>
				{loading ? (
					<div style={{ textAlign: "center", padding: 24 }}>
						<Loader size={24} />
					</div>
				) : activeItems.length === 0 ? (
					<div
						style={{
							padding: 24,
							textAlign: "center",
							color: "#888",
						}}
					>
						This folder is empty.
					</div>
				) : visibleItems.length === 0 ? (
					<div
						style={{
							padding: 24,
							textAlign: "center",
							color: "#888",
						}}
					>
						No matches found.
					</div>
				) : (
					visibleItems.map((rawItem) => {
						if (rawItem.type === "Folder") {
							return (
								<BrowseRow
									key={`folder:${rawItem.id}`}
									icon={
										iconMap({
											original: {
												type: "Folder",
												filename: rawItem.filename,
											},
										}).iconComponent
									}
									title={rawItem.filename}
									highlightQuery={filterText}
									onClick={() =>
										navigate({
											pathSegments: [
												...pathSegments,
												rawItem.filename,
											],
										})
									}
								/>
							);
						}
						const itemType = ITEM_TYPE_MAP[rawItem.type];
						const item = {
							itemType,
							id: rawItem.id,
							title: getTitle(rawItem),
							matterName: matter.name,
						};
						const key = contextItemKey(item);
						return (
							<BrowseRow
								key={key}
								icon={contextItemIcon(itemType, item.title)}
								title={item.title}
								subtitle={
									<RowSubtitle
										text={getSubtitle(rawItem)}
										item={item}
										highlightQuery={filterText}
									/>
								}
								highlightQuery={filterText}
								checked={
									!!selected[key] || existingKeys.has(key)
								}
								disabled={existingKeys.has(key)}
								onToggle={() => onToggle(item)}
								onPreview={() => onPreview(item)}
							/>
						);
					})
				)}
			</div>
		</div>
	);
};

export default MatterFolderBrowser;
